export interface RecheckInput {
  /** The saved article can be read (markdown and document id are present). */
  canRead: boolean;
  /** A job is running for this topic. */
  active: boolean;
  /** A readiness report is saved at all. */
  hasReport: boolean;
  /** The report describes the article as it stands. */
  isCurrent: boolean;
  /** The report passed every check. */
  ready: boolean;
}

export interface RecheckAction {
  show: boolean;
  /** `primary` while the saved verdict is missing, stale or failing; `secondary` once it is current and ready. */
  emphasis: 'primary' | 'secondary';
  label: string;
  /** What the action does when the verdict is current and ready, where the reason to press it is not obvious. */
  note: string | null;
}

/**
 * The recheck is offered whenever the saved article can be read and no job is running. It used to hide once
 * the readiness verdict was current and ready, so an operator could not refresh it after the evidence under
 * it changed (the sources a claim check read after the verdict was saved, a page that became readable). The
 * client cannot see the evidence store (service role only), so the action is always there and is only
 * quieter when nothing on the page says the verdict is out of date.
 */
export function recheckAction(input: RecheckInput): RecheckAction {
  if (!input.canRead || input.active)
    return { show: false, emphasis: 'primary', label: 'Recheck article', note: null };
  const settled = input.hasReport && input.isCurrent && input.ready;
  return {
    show: true,
    emphasis: settled ? 'secondary' : 'primary',
    label: input.hasReport ? 'Recheck article' : 'Check article',
    note: settled
      ? 'These checks are current. Recheck after the sources or the claim check change: it reads the stored sources again and runs the draft checks again. The editorial review of unchanged text is kept.'
      : null,
  };
}
